import React, { useState, useEffect, useRef } from "react";
import { Form, Input, Select, Button, message, Typography } from "antd";
import { getApi } from "../../helpers";
import { getAdminRoles } from "../../helpers/checklistApi";

const { Option } = Select;
const { Title } = Typography;

// Store managers always get this role; it's never offered for workers.
const STORE_MANAGER_ROLE_ID = 7;

export default function StaffForm({ initialData, onSave, onCancel }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [mobileLength, setMobileLength] = useState(0);
  const [storeOptions, setStoreOptions] = useState([]);
  const [loadingStores, setLoadingStores] = useState(false);
  const [roleOptions, setRoleOptions] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const rolesRequested = useRef(false);

  const staffType = Form.useWatch("staff_type", form);
  const accessLevel = Form.useWatch("access_level", form);
  const isOperations = staffType === "operations";

  useEffect(() => {
    fetchStores();
  }, []);

  // Roles come from the Operations API, so only fetch them once Operations is
  // chosen. A failed load is retried the next time Operations is picked.
  useEffect(() => {
    if (!isOperations || rolesRequested.current) return;
    rolesRequested.current = true;
    setLoadingRoles(true);
    getAdminRoles().then((roles) => {
      if (Array.isArray(roles)) {
        setRoleOptions(
          roles.filter((role) => role.isActive !== false && role.id !== STORE_MANAGER_ROLE_ID)
        );
      } else {
        rolesRequested.current = false;
      }
      setLoadingRoles(false);
    });
  }, [isOperations]);

  useEffect(() => {
    if (initialData) {
      form.setFieldsValue({
        username: initialData.username,
        staff_type: initialData.type,
        staff_name: initialData.name,
        staff_mobile: initialData.mobile,
        store_id: initialData.store_id,
      });
      setMobileLength(initialData.mobile?.length || 0);
    } else {
      form.resetFields();
      setMobileLength(0);
    }
  }, [initialData, form]);

  const fetchStores = async () => {
    try {
      setLoadingStores(true);
      const res = await getApi({}, "getStores", "dashboard/");
      if (res.data && res.data.length) {
        setStoreOptions(res.data);
      }
    } catch (error) {
      console.error("Error fetching stores:", error);
      message.error("Failed to load stores");
    } finally {
      setLoadingStores(false);
    }
  };

  // Clear fields that a change has just hidden, so they can't be submitted.
  const handleValuesChange = (changed) => {
    if ("staff_type" in changed) form.setFieldsValue({ access_level: undefined, role_ids: undefined });
    else if ("access_level" in changed) form.setFieldsValue({ role_ids: undefined });
  };

  const handleSubmit = async (values) => {
    const { access_level, role_ids, ...staffData } = values;
    if (staffData.staff_type === "operations") {
      staffData.access_level = access_level;
      if (access_level === "worker") staffData.role_ids = role_ids;
      else staffData.role_id = STORE_MANAGER_ROLE_ID;
    }

    setLoading(true);
    try {
      await onSave(staffData);
      message.success(
        initialData
          ? "Staff updated successfully!"
          : "Staff created successfully!"
      );
      form.resetFields();
      setMobileLength(0);
    } catch (error) {
      message.error(error?.message || "Failed to save staff details");
    }
    setLoading(false);
  };

  const handleCancel = () => {
    form.resetFields();
    setMobileLength(0);
    onCancel();
  };

  return (
    <div className="simple-staff-form">
      {/* <div className="form-header">
        <Title level={4} style={{ textAlign: "center", marginBottom: "32px" }}>
          {initialData ? "Edit Staff" : "Add new employee"}
        </Title>
      </div> */}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onValuesChange={handleValuesChange}
        className="staff-form"
      >
        <Form.Item
          name="username"
          rules={[
            { required: true, message: "Please enter username" },
            { min: 3, message: "Username must be at least 3 characters" },
          ]}
        >
          <Input placeholder="Username" className="form-input" />
        </Form.Item>

        <Form.Item
          name="staff_type"
          rules={[{ required: true, message: "Please select staff type" }]}
        >
          <Select placeholder="Type" className="form-select">
            <Option value="package">Package</Option>
            <Option value="delivery">Delivery</Option>
            <Option value="admin">Admin</Option>
            <Option value="operations">Operations</Option>
          </Select>
        </Form.Item>

        {isOperations && (
          <Form.Item
            name="access_level"
            rules={[{ required: true, message: "Please select access level" }]}
          >
            <Select placeholder="Access Level" className="form-select">
              <Option value="worker">Worker</Option>
              <Option value="store_manager">Store Manager</Option>
            </Select>
          </Form.Item>
        )}

        {isOperations && accessLevel === "worker" && (
          <Form.Item
            name="role_ids"
            rules={[{ required: true, type: "array", min: 1, message: "Please select at least one role" }]}
          >
            <Select
              mode="multiple"
              showSearch={false}
              placeholder="Roles"
              className="form-select"
              loading={loadingRoles}
              notFoundContent={loadingRoles ? "Loading..." : "No roles available"}
            >
              {roleOptions.map((role) => (
                <Option key={role.id} value={role.id}>
                  {role.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}

        <Form.Item
          name="staff_name"
          rules={[
            { required: true, message: "Please enter staff name" },
            { min: 2, message: "Name must be at least 2 characters" },
          ]}
        >
          <Input placeholder="Staff name" className="form-input" />
        </Form.Item>

        <Form.Item
          name="staff_mobile"
          rules={[
            { required: true, message: "Please enter mobile number" },
            {
              pattern: /^[0-9]{10}$/,
              message: "Please enter a valid 10-digit mobile number",
            },
          ]}
        >
          <Input
            placeholder="Mobile"
            maxLength={10}
            className="form-input"
            onChange={(e) => setMobileLength(e.target.value.length)}
          />
        </Form.Item>

        {!initialData && (
          <Form.Item
            name="password"
            rules={[
              { required: true, message: "Please enter password" },
              { min: 6, message: "Password must be at least 6 characters" },
            ]}
          >
            <Input.Password placeholder="Password" className="form-input" />
          </Form.Item>
        )}

        <Form.Item
          name="store_id"
          rules={[{ required: true, message: "Please select store" }]}
        >
          <Select 
            placeholder="Store" 
            className="form-select"
            loading={loadingStores}
            notFoundContent={loadingStores ? "Loading..." : "No stores available"}
          >
            {storeOptions.map((store) => (
              <Option key={store.store_id} value={store.store_id}>
                {store.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {/* <div className="mobile-counter">{mobileLength}/10</div> */}

        <Button
          type="primary"
          htmlType="submit"
          loading={loading}
          className="submit-button"
        >
          Submit
        </Button>
      </Form>
    </div>
  );
}
